import React, { useCallback, useEffect, useState } from 'react';

import { Group, Modal, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleCheckFilled, IconCircleOff, IconEdit, IconMail, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useListStaff from 'hooks/staff/useListStaff';
import useSendActivationEmail from 'hooks/users/useSendActivationEmail';
import { CellProps } from 'react-table';
import { StaffList } from 'types';
import { formatPhoneNumber } from 'utils/formatters';
import notificationHandler from 'utils/notifications';

import DeleteStaffModal from './DeleteStaffModal';

const StaffTableContainer = ({
  searchFilters,
  setOrderingValue,
  handleOpenStaffForm,
  setStaffId,
  permission = true,
}: {
  searchFilters: {
    searchTerm: string;
    isArrangeByLastName: boolean;
    loginEnabled: string;
  };
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
  handleOpenStaffForm: () => void;
  setStaffId: (id: string) => void;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();

  const [ordering, setOrdering] = useState('firstName');
  const [ascending, setAscending] = useState(true);
  const [tableData, setTableData] = useState<StaffList[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<StaffList>();
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const sendActivationEmail = useSendActivationEmail();

  const orderingValue =
    ordering === 'firstName' && searchFilters.isArrangeByLastName ? 'lastName' : ordering;

  const { data, fetchNextPage, hasNextPage, isSuccess, isLoading, isError, isFetchingNextPage } =
    useListStaff({
      ...searchFilters,
      ordering: ascending ? orderingValue : `-${orderingValue}`,
    });

  const handleHeaderClick = useCallback(
    (header: string): void => {
      let orderingTemp = header;
      if (header === 'firstName' && searchFilters.isArrangeByLastName) {
        orderingTemp = 'lastName';
      }
      const sameHeader = ordering === orderingTemp;
      const ascendingTemp = sameHeader ? !ascending : true;
      setOrdering(orderingTemp);
      setAscending(ascendingTemp);
      setOrderingValue(ascendingTemp ? orderingTemp : `-${orderingTemp}`);
    },
    [ascending, ordering, searchFilters.isArrangeByLastName, setOrdering, setAscending, setOrderingValue],
  );

  useEffect(() => {
    const header = searchFilters.isArrangeByLastName ? 'lastName' : 'firstName';
    setOrdering(header);
    setOrderingValue(ascending ? header : `-${header}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters]);

  const handleEdit = useCallback(
    (staff: StaffList): void => {
      setStaffId(staff.staffId);
      handleOpenStaffForm();
    },
    [handleOpenStaffForm, setStaffId],
  );

  const handleDelete = (staff: StaffList): void => {
    setSelectedStaff(staff);
    openDeleteModal();
  };

  const { successNotification, errorNotification } = notificationHandler();

  const handleSendLoginSetupEmail = (staff: StaffList): void => {
    setSelectedStaff(staff);
    sendActivationEmail.mutate(staff.user, {
      onSuccess: () => {
        successNotification('Login setup email successfully sent');
      },
      onError: () => {
        errorNotification('Login setup email failed to send');
      },
    });
  };

  const getMenuItems = (staff: StaffList): any => {
    const items = [
      {
        title: 'Edit',
        icon: <IconEdit size={theme.fontSizes.sm} />,
        onClick: () => handleEdit(staff),
      },
      {
        title: 'Delete',
        color: 'red',
        icon: <IconTrashX size={theme.fontSizes.sm} />,
        onClick: () => handleDelete(staff),
      },
    ];

    if (!staff.isActive) {
      items.splice(1, 0, {
        title: 'Send Login Setup Email',
        icon: <IconMail size={theme.fontSizes.sm} />,
        onClick: () => handleSendLoginSetupEmail(staff),
      });
    }

    return items;
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<StaffList>) => (
        <>
          <Text
            weight='bold'
            underline
            sx={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => handleEdit(row.original)}
          >
            {row.original.fullName}
          </Text>
        </>
      ),
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
    },
    {
      Header: 'Coach',
      accessor: 'isCoach' as const,
      Cell: ({ value }: CellProps<StaffList>) =>
        value ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.lg} color='gray' />
        ),
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      Cell: ({ value }: CellProps<StaffList>) => formatPhoneNumber(value),
    },
    {
      Header: 'Date of Birth',
      accessor: 'dob' as const,
      Cell: ({ value }: CellProps<StaffList>) => (value ? dayjs(value).format(dateFormat) : null),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<StaffList>) => (
        <Group position='right' data-cy='actionsStaff'>
          <Menu disabled={!permission} position='bottom-end' items={getMenuItems(row.original)} />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'loginEnabled']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
      {selectedStaff && (
        <Modal
          opened={isDeleteModalOpen}
          onClose={closeDeleteModal}
          title={<Title order={4}>Delete Staff</Title>}
          trapFocus
          centered
          size='auto'
        >
          <DeleteStaffModal staff={selectedStaff} handleClose={closeDeleteModal} />
        </Modal>
      )}
    </>
  );
};

export default StaffTableContainer;
